"use client"

import { useState, useMemo } from "react"
import Link from "next/link"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select"
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@/components/ui/table"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import type {
  WoodConnectionData,
  WoodConnectionItem,
} from "../types/wood-connection"

/** `[label](url)` の Markdown 文字列 → { label, href } へパース */
function parseMarkdownLink(md: string): { label: string; href: string } | null {
  const m = md.match(/\[([^\]]+)]\(([^)]+)\)/)
  return m ? { label: m[1], href: m[2] } : null
}

interface Props {
  /** テーブルに渡すデータ。未指定でも空配列で処理される */
  data?: WoodConnectionData
}

export function WoodConnectionTable({ data = [] }: Props) {
  /* ---------- 大分類リスト ---------- */
  const categories = useMemo<string[]>(() => {
    const unique = Array.from(new Set(data.map((d) => d.category))).sort()
    return ["すべて", ...unique]
  }, [data])

  const [selectedCat, setSelectedCat] = useState<string>("すべて")

  /* ---------- 小分類リスト ---------- */
  const subCats = useMemo<string[]>(() => {
    if (selectedCat === "すべて") return []
    return Array.from(
      new Set(
        data
          .filter((d) => d.category === selectedCat)
          .map((d) => d.subCategory),
      ),
    ).sort()
  }, [data, selectedCat])

  const [selectedSub, setSelectedSub] = useState<string>("すべて")

  /* ---------- 絞り込み ---------- */
  const filtered = useMemo<WoodConnectionItem[]>(() => {
    return data.filter(
      (d) =>
        (selectedCat === "すべて" || d.category === selectedCat) &&
        (selectedSub === "すべて" || d.subCategory === selectedSub),
    )
  }, [data, selectedCat, selectedSub])

  /* ---------- UI ---------- */
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>使用箇所別検索</CardTitle>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* ----- フィルター ----- */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* 大分類セレクト */}
          <Select
            value={selectedCat}
            onValueChange={(v) => {
              setSelectedCat(v)
              setSelectedSub("すべて") // 小分類もリセット
            }}
          >
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="大分類を選択" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* 小分類セレクト  */}
          <Select
            disabled={selectedCat === "すべて"}
            value={selectedSub}
            onValueChange={(v) => setSelectedSub(v)}
          >
            <SelectTrigger className="w-full sm:w-[240px]">
              <SelectValue placeholder="小分類を選択" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="すべて">すべて</SelectItem>
              {subCats.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* ----- テーブル ----- */}
        <ScrollArea className="border rounded-lg">
          <Table>
            <TableHeader className="sticky top-0 bg-primary/10 z-10">
              <TableRow>
                <TableHead className="min-w-[160px]">製品名</TableHead>
                <TableHead className="w-[90px]">メーカー</TableHead>
                <TableHead className="w-[110px]">短期耐力</TableHead>
                <TableHead className="w-[60px]">在庫</TableHead>
                <TableHead>備考・特長</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filtered.map((item) => {
                const md = parseMarkdownLink(item.name)
                return (
                  <TableRow
                    /* 3 つを連結してキーを一意にすることで
                       React が行を誤って再利用する問題を防ぐ */
                    key={`${item.category}-${item.subCategory}-${item.name}`}
                    className="hover:bg-muted/50 transition-colors"
                  >
                    <TableCell>
                      {md ? (
                        <Link
                          href={md.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline text-primary"
                        >
                          {md.label}
                        </Link>
                      ) : (
                        item.name
                      )}
                    </TableCell>
                    <TableCell>{item.maker}</TableCell>
                    <TableCell>{item.strength || "－"}</TableCell>
                    <TableCell>
                      {item.stock === "△" ? (
                        <Badge variant="secondary">要確認</Badge>
                      ) : (
                        item.stock || "－"
                      )}
                    </TableCell>
                    <TableCell>{item.note}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </ScrollArea>

        {/* 件数表示 */}
        <p className="text-sm text-muted-foreground">
          {filtered.length} / {data.length} 件表示
        </p>
      </CardContent>
    </Card>
  )
}
